// =============================================================================
// IntegrationsPage.js — External integration management panel
// =============================================================================
// Manages the three integration connections the portal relies on:
//   1. Pimberly PIM  — product/pack catalogue source (syncs Products & Packs tables)
//   2. On-Prem SQL   — warehouse SQL server (syncs stock levels and site data)
//   3. HubSpot CRM   — push finalized order lines back to the HubSpot deal
//
// HOW IT IS ORGANISED:
//   • Status overview cards at the top (always visible) show last-sync time + badge
//   • Tabs switch between integration-specific panels:
//       pimberly → connection config, sync trigger, data preview
//       onprem   → SQL connection config, sync trigger, stock/customer data browser
//       hubspot  → connection test, preview & push lines to a deal
//       logs     → combined sync log from all integrations
//
// TO ADD A NEW INTEGRATION:
//   1. Add a tab button in the tabs array
//   2. Add a state section below for the new config form
//   3. Add handlers (handleTestXxx, handleSyncXxx, etc.)
//   4. Add the tab content render block ({activeTab === 'myint' && (...)})
//   5. Add API functions in api.js and a route in server/routes/integrations.js
//   6. Register an instance in server/index.js and expose it via app.locals
//
// API calls used: getIntegrationStatus, getIntegrationLogs, syncAll,
//                 testPimberlyConnection, syncPimberly, updatePimberlyConfig, previewPimberly,
//                 testOnPremConnection, syncOnPrem, updateOnPremConfig,
//                 hubspotTestConnection, hubspotPreviewLines, hubspotPushLines, hubspotGetLogs
// =============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import {
  getIntegrationStatus,
  testPimberlyConnection,
  syncPimberly,
  updatePimberlyConfig,
  previewPimberly,
  testOnPremConnection,
  syncOnPrem,
  updateOnPremConfig,
  syncAll,
  getIntegrationLogs,
  hubspotTestConnection,
  hubspotPreviewLines,
  hubspotPushLines,
  hubspotGetLogs,
} from '../api';

export default function IntegrationsPage() {
  // ---- UI state ----
  const [activeTab, setActiveTab] = useState('pimberly'); // which integration tab is visible
  const [status, setStatus] = useState(null);  // combined status from all integrations
  const [logs, setLogs] = useState([]);         // sync log entries
  const [loading, setLoading] = useState({});   // per-operation loading key (e.g. loading.syncPim)
  const [message, setMessage] = useState(null); // flash notification { text, type: 'success'|'error' }
  const [preview, setPreview] = useState(null); // Pimberly preview data panel

  // ---- Pimberly config form state ----
  // To add a new Pimberly config field: add it here, include it in handleSavePimConfig,
  // and add the matching input in the Pimberly tab JSX below.
  const [pimConfig, setPimConfig] = useState({
    apiKey: '',
    baseUrl: 'https://sandbox.pimberly.io/api/v2.2',
    channelId: ''
  });

  // ---- On-Prem SQL config form state ----
  // To change SQL authentication method, update this state and the server-side OnPremSqlService.
  const [opConfig, setOpConfig] = useState({
    server: '', database: '', user: '', password: '', port: '1433',
    encrypt: false, trustServerCertificate: true,
    customersQuery: '', pricesQuery: '', stockQuery: '' // optional override SQL queries
  });

  // ---- HubSpot push/preview state ----
  const [hsOrderId, setHsOrderId] = useState('');     // local order ID to push
  const [hsDealId, setHsDealId] = useState('');       // HubSpot deal ID to push to
  const [hsPreview, setHsPreview] = useState(null);   // preview data before push
  const [hsPushResult, setHsPushResult] = useState(null); // result after push
  const [hsLogs, setHsLogs] = useState([]);           // recent HubSpot API call log

  // fetchStatus — loads combined integration status and pre-fills config forms
  // with any values already saved on the server (so the UI shows current config).
  const fetchStatus = useCallback(async () => {
    try {
      const res = await getIntegrationStatus();
      setStatus(res.data);
      // Pre-fill Pimberly form with saved server values (if available)
      if (res.data.pimberly) {
        setPimConfig(prev => ({
          ...prev,
          baseUrl: res.data.pimberly.baseUrl || prev.baseUrl,
          channelId: res.data.pimberly.channelId !== 'not set' ? res.data.pimberly.channelId : prev.channelId,
        }));
      }
      // Pre-fill On-Prem form with saved server values (if available)
      if (res.data.onprem) {
        setOpConfig(prev => ({
          ...prev,
          server: res.data.onprem.server !== 'not configured' ? res.data.onprem.server : prev.server,
          database: res.data.onprem.database !== 'not configured' ? res.data.onprem.database : prev.database,
        }));
      }
    } catch (e) { console.error(e); }
  }, []);

  // fetchLogs — loads the recent integration sync log (last 100 entries)
  const fetchLogs = useCallback(async () => {
    try {
      const res = await getIntegrationLogs(100);
      setLogs(res.data);
    } catch (e) { console.error(e); }
  }, []);

  // Load status and logs on mount
  useEffect(() => { fetchStatus(); fetchLogs(); }, [fetchStatus, fetchLogs]);

  // flash — shows a temporary success or error banner at the top of the content area.
  // Auto-dismisses after 6 seconds.
  const flash = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 6000);
  };

  // withLoading — utility wrapper that sets a per-key loading flag around an async operation.
  // key matches a property in the `loading` object, e.g. 'syncPim', 'testOp'.
  const withLoading = async (key, fn) => {
    setLoading(prev => ({ ...prev, [key]: true }));
    try { await fn(); } finally { setLoading(prev => ({ ...prev, [key]: false })); }
  };

  // ==========================================================================
  // PIMBERLY HANDLERS
  // ==========================================================================

  // handleTestPimberly — verifies the Pimberly API key and base URL without syncing
  const handleTestPimberly = () => withLoading('testPim', async () => {
    try {
      const res = await testPimberlyConnection();
      flash(res.data.success ? `✓ ${res.data.message}` : `✗ ${res.data.message}`, res.data.success ? 'success' : 'error');
    } catch (e) { flash(`✗ ${e.response?.data?.message || e.message}`, 'error'); }
  });

  // handleSyncPimberly — pulls all products and packs from Pimberly into the local DB
  const handleSyncPimberly = () => withLoading('syncPim', async () => {
    try {
      const res = await syncPimberly();
      if (res.data.success) {
        flash(`✓ Synced! Products: +${res.data.products?.added || 0} updated ${res.data.products?.updated || 0}, Packs: +${res.data.packs?.added || 0}`);
      } else {
        flash(`✗ ${res.data.message}`, 'error');
      }
      fetchStatus(); fetchLogs(); // refresh status cards and log
    } catch (e) { flash(`✗ ${e.response?.data?.message || e.message}`, 'error'); }
  });

  // handleSavePimConfig — persists the Pimberly config (apiKey, baseUrl, channelId) on the server
  const handleSavePimConfig = () => withLoading('savePim', async () => {
    try {
      await updatePimberlyConfig(pimConfig);
      flash('✓ Pimberly configuration saved');
      fetchStatus();
    } catch (e) { flash(`✗ ${e.message}`, 'error'); }
  });

  // handlePreviewPimberly — fetches the first 20 items from Pimberly without importing them,
  // useful for verifying the field mapping before doing a full sync
  const handlePreviewPimberly = () => withLoading('previewPim', async () => {
    try {
      const res = await previewPimberly();
      setPreview(res.data);
    } catch (e) { flash(`✗ ${e.response?.data?.message || e.message}`, 'error'); }
  });

  // ==========================================================================
  // ON-PREM SQL HANDLERS
  // ==========================================================================

  // handleTestOnPrem — verifies the SQL connection string without running a sync
  const handleTestOnPrem = () => withLoading('testOp', async () => {
    try {
      const res = await testOnPremConnection();
      flash(res.data.success
        ? `✓ ${res.data.message} — Server: ${res.data.serverName}`
        : `✗ ${res.data.message}`, res.data.success ? 'success' : 'error');
    } catch (e) { flash(`✗ ${e.response?.data?.message || e.message}`, 'error'); }
  });

  // handleSyncOnPrem — pulls site and stock data from the warehouse SQL server.
  // The result summary shows how many stock entries were matched, zero-filled, or skipped.
  const handleSyncOnPrem = () => withLoading('syncOp', async () => {
    try {
      const res = await syncOnPrem();
      if (res.data.success) {
        const st = res.data.stock || {};
        flash(`✓ Stock synced! Sites: +${res.data.sites?.added || 0} ↻${res.data.sites?.updated || 0} | Stock entries: ${st.total || 0} (${st.matched || 0} with qty, ${st.zeroFilled || 0} zero-filled${st.skippedSkus ? `, ${st.skippedSkus} unmatched SKUs` : ''})`);
      } else {
        flash(`✗ ${res.data.message || 'Sync failed — check server logs'}`, 'error');
      }
      fetchStatus(); fetchLogs();
    } catch (e) { flash(`✗ ${e.response?.data?.message || e.message}`, 'error'); }
  });

  // handleSaveOpConfig — persists the On-Prem SQL connection config on the server
  const handleSaveOpConfig = () => withLoading('saveOp', async () => {
    try {
      await updateOnPremConfig(opConfig);
      flash('✓ On-prem SQL configuration saved');
      fetchStatus();
    } catch (e) { flash(`✗ ${e.message}`, 'error'); }
  });

  // handleSyncAll — runs both Pimberly and On-Prem syncs in sequence.
  // The result shows a per-integration success flag.
  const handleSyncAll = () => withLoading('syncAll', async () => {
    try {
      const res = await syncAll();
      const d = res.data;
      flash(`Full sync complete — Pimberly: ${d.pimberly?.success ? '✓' : '✗'}, On-Prem: ${d.onprem?.success ? '✓' : '✗'}`);
      fetchStatus(); fetchLogs();
    } catch (e) { flash(`✗ ${e.message}`, 'error'); }
  });

  // ==========================================================================
  // HUBSPOT HANDLERS
  // ==========================================================================

  // handleHsTestConnection — pings the HubSpot API with the configured token to verify auth
  const handleHsTestConnection = () => withLoading('hsTest', async () => {
    try {
      const res = await hubspotTestConnection();
      flash(res.data.success ? `✓ ${res.data.message}` : `✗ ${res.data.message}`, res.data.success ? 'success' : 'error');
    } catch (e) { flash(`✗ ${e.response?.data?.message || e.message}`, 'error'); }
  });

  // handleHsPreview — loads the line items for an order without pushing them to HubSpot.
  // Also auto-fills the deal ID field if the order has a HubSpotDealId set.
  const handleHsPreview = () => withLoading('hsPreview', async () => {
    try {
      if (!hsOrderId) { flash('Enter an Order ID first', 'error'); return; }
      const res = await hubspotPreviewLines(hsOrderId);
      setHsPreview(res.data);
      setHsPushResult(null); // clear previous push result
      // Use deal ID from the order if the user hasn't manually overridden it
      if (res.data.dealId && !hsDealId) setHsDealId(res.data.dealId);
    } catch (e) { flash(`✗ ${e.response?.data?.error || e.message}`, 'error'); }
  });

  // handleHsPush — creates/replaces line items on the specified HubSpot deal.
  // Both orderId and dealId are required. After push, fetches the latest HS logs.
  const handleHsPush = () => withLoading('hsPush', async () => {
    try {
      if (!hsOrderId) { flash('Enter an Order ID first', 'error'); return; }
      if (!hsDealId) { flash('Enter a HubSpot Deal ID', 'error'); return; }
      const res = await hubspotPushLines(hsOrderId, hsDealId);
      setHsPushResult(res.data);
      if (res.data.success) {
        flash(`✓ ${res.data.message}`);
      } else {
        flash(`✗ ${res.data.message}`, 'error');
      }
      // Refresh HubSpot action log after push
      try { const lr = await hubspotGetLogs(50); setHsLogs(lr.data); } catch (_) {}
    } catch (e) { flash(`✗ ${e.response?.data?.error || e.message}`, 'error'); }
  });

  // handleHsFetchLogs — manually refreshes the HubSpot API call log table
  const handleHsFetchLogs = async () => {
    try { const res = await hubspotGetLogs(50); setHsLogs(res.data); } catch (_) {}
  };

  // ==========================================================================
  // StatusBadge — small inline component maps a status string to a coloured badge.
  // status values: 'idle' | 'running' | 'success' | 'error'
  // CSS badge classes are defined in App.css (hs-badge-available, hs-badge-outofstock, etc.)
  // ==========================================================================
  const StatusBadge = ({ value }) => {
    const map = { idle: 'unchecked', running: 'pending', success: 'available', error: 'outofstock' };
    return <span className={`hs-badge hs-badge-${map[value] || 'unchecked'}`}>{value || 'unknown'}</span>;
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
      {/* ---- Page header ---- */}
      <div className="top-header">
        <h1>Integrations</h1>
        <div className="top-header-actions">
          {/* Sync All triggers both Pimberly and On-Prem syncs in one click */}
          <button className="hs-btn hs-btn-primary" onClick={handleSyncAll} disabled={loading.syncAll}>
            {loading.syncAll ? 'Syncing…' : '⟳ Sync All'}
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* ---- Flash notification banner ---- */}
        {/* Shown temporarily after any operation; auto-dismisses after 6 s */}
        {message && (
          <div className={`hs-alert hs-alert-${message.type === 'error' ? 'danger' : 'success'} mb-md`}>
            {message.text}
          </div>
        )}

        {/* ---- Integration status overview cards ---- */}
        {/* Shows last-sync timestamp and current status for each integration */}
        <div className="stats-row">
          <div className="stat-card flex-between">
            <div>
              <div className="stat-label">PIMBERLY PIM</div>
              <div style={{ fontSize: 14, marginTop: 4 }}>
                Last sync: {status?.pimberly?.lastSync ? new Date(status.pimberly.lastSync).toLocaleString() : 'Never'}
              </div>
            </div>
            <StatusBadge value={status?.pimberly?.status} />
          </div>
          <div className="stat-card flex-between">
            <div>
              <div className="stat-label">ON-PREM SQL GATEWAY</div>
              <div style={{ fontSize: 14, marginTop: 4 }}>
                Last sync: {status?.onprem?.lastSync ? new Date(status.onprem.lastSync).toLocaleString() : 'Never'}
              </div>
            </div>
            <StatusBadge value={status?.onprem?.status} />
          </div>
          <div className="stat-card flex-between">
            <div>
              <div className="stat-label">HUBSPOT CRM</div>
              <div style={{ fontSize: 14, marginTop: 4 }}>
                Status: {status?.hubspot || 'idle'}
              </div>
            </div>
            <StatusBadge value={status?.hubspot} />
          </div>
        </div>

        {/* ---- Tab bar ---- */}
        {/* To add a new integration tab, add an entry to this array and a matching render block below */}
        <div className="hs-tabs">
          {[
            { key: 'pimberly', label: '🔗 Pimberly PIM' },
            { key: 'onprem',   label: '🗄️ On-Prem SQL' },
            { key: 'hubspot',  label: '🟠 HubSpot' },
            { key: 'logs',     label: '📋 Sync Logs' },
          ].map(tab => (
            <button key={tab.key}
              className={`hs-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ======================= PIMBERLY TAB ======================= */}
        {activeTab === 'pimberly' && (
          <div>
            {/* Connection Settings — edit apiKey, baseUrl, channelId */}
            <div className="hs-card mb-lg">
              <div className="hs-card-header"><h2>Connection Settings</h2></div>
              <div className="hs-card-body">
                <div className="hs-form-group">
                  <label>API Key</label>
                  <input className="hs-input" type="password" placeholder="Enter Pimberly API key"
                    value={pimConfig.apiKey} onChange={e => setPimConfig({ ...pimConfig, apiKey: e.target.value })} />
                </div>
                <div className="hs-form-row">
                  <div className="hs-form-group">
                    <label>Base URL</label>
                    <input className="hs-input" placeholder="https://sandbox.pimberly.io/api/v2.2"
                      value={pimConfig.baseUrl} onChange={e => setPimConfig({ ...pimConfig, baseUrl: e.target.value })} />
                  </div>
                  <div className="hs-form-group">
                    <label>Channel ID (optional)</label>
                    <input className="hs-input" placeholder="Channel ID"
                      value={pimConfig.channelId} onChange={e => setPimConfig({ ...pimConfig, channelId: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-sm mt-sm">
                  <button className="hs-btn hs-btn-primary" onClick={handleSavePimConfig} disabled={loading.savePim}>
                    {loading.savePim ? 'Saving…' : 'Save Config'}
                  </button>
                  <button className="hs-btn hs-btn-secondary" onClick={handleTestPimberly} disabled={loading.testPim}>
                    {loading.testPim ? 'Testing…' : 'Test Connection'}
                  </button>
                </div>
              </div>
            </div>

            {/* Sync panel — buttons to run a full sync or preview the data first */}
            <div className="hs-card mb-lg">
              <div className="hs-card-header"><h2>Product &amp; Pack Sync</h2></div>
              <div className="hs-card-body">
                <p className="text-muted mb-md">
                  Pull products and packs from Pimberly into the local database. Products marked as bundles in Pimberly will be imported as Packs, others as Products.
                </p>
                <div className="flex gap-sm">
                  <button className="hs-btn hs-btn-primary" onClick={handleSyncPimberly} disabled={loading.syncPim}>
                    {loading.syncPim ? 'Syncing…' : '⟳ Sync Products & Packs'}
                  </button>
                  <button className="hs-btn hs-btn-secondary" onClick={handlePreviewPimberly} disabled={loading.previewPim}>
                    {loading.previewPim ? 'Loading…' : '👁 Preview Data'}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview panel — shown after clicking 'Preview Data'. Shows the first 20 items
                 without actually importing them, useful for checking field mapping. */}
            {preview && (
              <div className="hs-card mb-lg">
                <div className="hs-card-header">
                  <h2>Pimberly Preview ({preview.totalInPimberly} items total)</h2>
                  <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => setPreview(null)}>Close</button>
                </div>
                <div className="hs-card-body" style={{ maxHeight: 400, overflow: 'auto' }}>
                  <table className="hs-table">
                    <thead>
                      <tr>
                        <th>Type</th><th>SKU</th><th>Name</th><th>Price</th><th>SAP Code</th><th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview?.map((item, i) => (
                        <tr key={i}>
                          <td><span className={`hs-badge hs-badge-${item.type === 'pack' ? 'pack' : 'product'}`}>{item.type}</span></td>
                          <td className="font-mono">{item.SKU || item.PackSKU}</td>
                          <td>{item.ProductName || item.PackName}</td>
                          <td>£{(item.UnitPrice || item.PackPrice || 0).toFixed(2)}</td>
                          <td className="font-mono">{item.SAPMaterialCode || item.SAPPackCode || '—'}</td>
                          <td>{item.Category || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Field mapping reference — static doc table showing Pimberly → DB field mapping.
                 To change a mapping, update server/integrations/pimberly.js :: mapProduct(). */}
            <div className="hs-card">
              <div className="hs-card-header"><h2>Field Mapping Reference</h2></div>
              <div className="hs-card-body">
                <table className="hs-table">
                  <thead><tr><th>Our Field</th><th>Pimberly Attribute</th><th>Description</th></tr></thead>
                  <tbody>
                    <tr><td>SKU</td><td className="font-mono">primaryId</td><td>Pimberly primary identifier</td></tr>
                    <tr><td>ProductName</td><td className="font-mono">product_name</td><td>Display name</td></tr>
                    <tr><td>UnitPrice</td><td className="font-mono">price</td><td>Base selling price</td></tr>
                    <tr><td>SAPMaterialCode</td><td className="font-mono">sap_material_code</td><td>SAP integration code</td></tr>
                    <tr><td>Category</td><td className="font-mono">category</td><td>Product category</td></tr>
                    <tr><td>Is Pack/Bundle</td><td className="font-mono">is_bundle</td><td>true = import as Pack</td></tr>
                    <tr><td>Pack Items</td><td className="font-mono">pack_items</td><td>JSON array or CSV of child SKU:qty</td></tr>
                  </tbody>
                </table>
                <p className="text-muted text-sm mt-md">
                  Field mappings can be customised via the API: PUT /api/integrations/pimberly/config with a fieldMapping object.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ======================= ON-PREM SQL TAB ======================= */}
        {activeTab === 'onprem' && (
          <div>
            {/* Connection config — SQL auth fields.
                 TrustServerCertificate should be true for self-signed on-prem certs. */}
            <div className="hs-card mb-lg">
              <div className="hs-card-header"><h2>SQL Server Connection</h2></div>
              <div className="hs-card-body">
                <div className="hs-form-row">
                  <div className="hs-form-group">
                    <label>Server / Host</label>
                    <input className="hs-input" placeholder="e.g. 192.168.1.100 or server-name\instance"
                      value={opConfig.server} onChange={e => setOpConfig({ ...opConfig, server: e.target.value })} />
                  </div>
                  <div className="hs-form-group">
                    <label>Port</label>
                    <input className="hs-input" type="number" value={opConfig.port}
                      onChange={e => setOpConfig({ ...opConfig, port: e.target.value })} />
                  </div>
                </div>
                <div className="hs-form-group">
                  <label>Database</label>
                  <input className="hs-input" placeholder="e.g. SAPData or ERP_Prod"
                    value={opConfig.database} onChange={e => setOpConfig({ ...opConfig, database: e.target.value })} />
                </div>
                <div className="hs-form-row">
                  <div className="hs-form-group">
                    <label>Username</label>
                    <input className="hs-input" placeholder="SQL login user"
                      value={opConfig.user} onChange={e => setOpConfig({ ...opConfig, user: e.target.value })} />
                  </div>
                  <div className="hs-form-group">
                    <label>Password</label>
                    <input className="hs-input" type="password" placeholder="SQL login password"
                      value={opConfig.password} onChange={e => setOpConfig({ ...opConfig, password: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-md mb-md">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={opConfig.encrypt}
                      onChange={e => setOpConfig({ ...opConfig, encrypt: e.target.checked })} />
                    Encrypt connection
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                    <input type="checkbox" checked={opConfig.trustServerCertificate}
                      onChange={e => setOpConfig({ ...opConfig, trustServerCertificate: e.target.checked })} />
                    Trust server certificate
                  </label>
                </div>
                <div className="flex gap-sm">
                  <button className="hs-btn hs-btn-primary" onClick={handleSaveOpConfig} disabled={loading.saveOp}>
                    {loading.saveOp ? 'Saving…' : 'Save Config'}
                  </button>
                  <button className="hs-btn hs-btn-secondary" onClick={handleTestOnPrem} disabled={loading.testOp}>
                    {loading.testOp ? 'Testing…' : 'Test Connection'}
                  </button>
                </div>
              </div>
            </div>

            {/* Data Sync — pulls from dbo.StockData into the app DB.
                 To customise the SQL query, set customersQuery/stockQuery in opConfig. */}
            <div className="hs-card mb-lg">
              <div className="hs-card-header"><h2>Data Sync</h2></div>
              <div className="hs-card-body">
                <p className="text-muted mb-md">
                  Pull stock levels from the on-premises SQL Server (dbo.StockData) into the app database.
                  Creates franchise sites automatically and builds a full product × site stock matrix.
                  Products missing from the on-prem data get stock quantity 0.
                </p>
                <div className="flex gap-sm">
                  <button className="hs-btn hs-btn-primary" onClick={handleSyncOnPrem} disabled={loading.syncOp}>
                    {loading.syncOp ? 'Syncing…' : '⟳ Sync Stock Data'}
                  </button>
                </div>
              </div>
            </div>


          </div>
        )}

        {/* ======================= HUBSPOT TAB ======================= */}
        {activeTab === 'hubspot' && (
          <div>
            {/* Connection test — verifies HUBSPOT_ACCESS_TOKEN is valid */}
            <div className="hs-card mb-lg">
              <div className="hs-card-header"><h2>HubSpot Connection</h2></div>
              <div className="hs-card-body">
                <p className="text-muted mb-md">
                  Tests the Private App access token configured via <code>HUBSPOT_ACCESS_TOKEN</code> environment variable.
                </p>
                <button className="hs-btn hs-btn-secondary" onClick={handleHsTestConnection} disabled={loading.hsTest}>
                  {loading.hsTest ? 'Testing…' : 'Test Connection'}
                </button>
              </div>
            </div>

            {/* Push line items — sends order lines to a HubSpot deal as line items.
                 dealId is auto-filled if the order has a HubSpotDealId set. */}
            <div className="hs-card mb-lg">
              <div className="hs-card-header"><h2>Push Order Lines to HubSpot</h2></div>
              <div className="hs-card-body">
                <p className="text-muted mb-md">
                  Select an order and push its top-level line items (SKU, Qty, Line Total) to an existing HubSpot deal.
                  Substitution data will be written to the deal description.
                </p>
                <div className="hs-form-row">
                  <div className="hs-form-group">
                    <label>Order ID (local)</label>
                    <input className="hs-input" type="number" placeholder="e.g. 12"
                      value={hsOrderId} onChange={e => setHsOrderId(e.target.value)} />
                  </div>
                  <div className="hs-form-group">
                    <label>HubSpot Deal ID</label>
                    <input className="hs-input" placeholder="e.g. 19827364510"
                      value={hsDealId} onChange={e => setHsDealId(e.target.value)} />
                    <span className="text-muted text-sm">Auto-filled from order if linked</span>
                  </div>
                </div>
                <div className="flex gap-sm mt-sm">
                  <button className="hs-btn hs-btn-secondary" onClick={handleHsPreview} disabled={loading.hsPreview || !hsOrderId}>
                    {loading.hsPreview ? 'Loading…' : '👁 Preview'}
                  </button>
                  <button className="hs-btn hs-btn-primary" onClick={handleHsPush}
                    disabled={loading.hsPush || !hsOrderId || !hsDealId}>
                    {loading.hsPush ? 'Pushing…' : '🚀 Push to HubSpot'}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview table — shows the line items that WOULD be pushed to HubSpot */}
            {hsPreview && (
              <div className="hs-card mb-lg">
                <div className="hs-card-header">
                  <h2>Preview — Order {hsPreview.orderNumber} ({hsPreview.lineCount} lines)</h2>
                  <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => setHsPreview(null)}>Close</button>
                </div>
                <div className="hs-card-body">
                  {hsPreview.substitutions && (
                    <div className="hs-alert hs-alert-info mb-md">
                      <strong>Substitutions:</strong> {hsPreview.substitutions}
                    </div>
                  )}
                  <table className="hs-table">
                    <thead>
                      <tr><th>#</th><th>SKU</th><th>Name</th><th>Qty</th><th>Unit Price</th><th>Line Total</th></tr>
                    </thead>
                    <tbody>
                      {hsPreview.lines.map((l, i) => (
                        <tr key={i}>
                          <td>{l.LineNumber}</td>
                          <td className="font-mono">{l.SKU}</td>
                          <td>{l.Name}</td>
                          <td>{l.Quantity}</td>
                          <td>£{(l.UnitPrice || 0).toFixed(2)}</td>
                          <td className="font-mono">£{(l.LineTotal || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700 }}>
                        <td colSpan={5} style={{ textAlign: 'right' }}>Total</td>
                        <td className="font-mono">£{(hsPreview.totalAmount || 0).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Push result banner — shown after handleHsPush; green = success, red = failure.
                 hsPushResult.lineItemsCreated/Failed counts reflect the per-line-item HubSpot API calls. */}
            {hsPushResult && (
              <div className={`hs-alert ${hsPushResult.success ? 'hs-alert-success' : 'hs-alert-danger'} mb-lg`}>
                <strong>{hsPushResult.success ? '✓ Success' : '✗ Failed'}:</strong> {hsPushResult.message}
                {hsPushResult.lineItemsCreated != null && (
                  <span> — {hsPushResult.lineItemsCreated} created, {hsPushResult.lineItemsFailed} failed</span>
                )}
              </div>
            )}

            {/* HubSpot API call log — shows the last 50 calls.
                 Populated on mount via handleHsFetchLogs; also refreshes after each push. */}
            <div className="hs-card">
              <div className="hs-card-header">
                <h2>HubSpot Logs</h2>
                <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={handleHsFetchLogs}>↻ Refresh</button>
              </div>
              <div className="hs-card-body" style={{ maxHeight: 400, overflow: 'auto' }}>
                {hsLogs.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <p>No HubSpot logs yet. Push an order or test the connection to see activity.</p>
                  </div>
                ) : (
                  <table className="hs-table">
                    <thead><tr><th>Time</th><th>Level</th><th>Message</th></tr></thead>
                    <tbody>
                      {hsLogs.map((log, i) => (
                        <tr key={i}>
                          <td className="text-sm" style={{ whiteSpace: 'nowrap' }}>
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td>
                            <span className={log.level === 'error' ? 'text-danger' : log.level === 'warn' ? 'text-warning' : 'text-success'}
                              style={{ fontWeight: 600, fontSize: 12 }}>
                              {log.level.toUpperCase()}
                            </span>
                          </td>
                          <td>{log.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ======================= LOGS TAB ======================= */}
        {/* Combined sync log from Pimberly and On-Prem operations.
             To add a log entry from a new integration, write to the IntegrationLogs table
             via dal.logIntegrationEvent() in server/db/dal.js */}
        {activeTab === 'logs' && (
          <div className="hs-card">
            <div className="hs-card-header">
              <h2>Sync Logs</h2>
              <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={fetchLogs}>↻ Refresh</button>
            </div>
            <div className="hs-card-body" style={{ maxHeight: 600, overflow: 'auto' }}>
              {logs.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <h3>No sync logs yet</h3>
                  <p>Run a sync to see activity here.</p>
                </div>
              ) : (
                <table className="hs-table">
                  <thead>
                    <tr><th>Time</th><th>Source</th><th>Level</th><th>Message</th></tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={i}>
                        <td className="text-sm" style={{ whiteSpace: 'nowrap' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td>
                          <span className={`hs-badge ${log.source === 'pimberly' ? 'hs-badge-pack' : 'hs-badge-product'}`}>
                            {log.source}
                          </span>
                        </td>
                        <td>
                          <span className={
                            log.level === 'error' ? 'text-danger' :
                            log.level === 'warn'  ? 'text-warning' :
                            'text-success'
                          } style={{ fontWeight: 600, fontSize: 12 }}>
                            {log.level.toUpperCase()}
                          </span>
                        </td>
                        <td>{log.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
